import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { updateUser, checkUserDeletable } from '../../services/api';
import { formatKenyanPhone, isValidKenyanPhone } from '../../utils/phoneUtils';

const EditUserModal = ({ open, onClose, user, onUpdate, companies, isSuperAdmin, isCusAdmin, currentUserCompanyId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    company_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canChangeRole, setCanChangeRole] = useState(true);
  const [originalRole, setOriginalRole] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [formValid, setFormValid] = useState(true);

  // Reset all state when modal closes
  const handleClose = () => {
    // Reset all state variables
    setError('');
    setSuccess('');
    setLoading(false);
    setCanChangeRole(true);
    setOriginalRole('');
    setPhoneError('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      company_id: ''
    });
    // Call the parent onClose function
    onClose();
  };

  useEffect(() => {
    if (user && open) { // Only load user data if modal is open
      // Format the phone number if exists
      const formattedPhone = user.phone ? formatKenyanPhone(user.phone) : '+254';
      
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: formattedPhone,
        role: user.role || '',
        company_id: user.company_id || ''
      });
      setOriginalRole(user.role);

      // Check if user can change role
      const checkRoleChange = async () => {
        // First check if cus-admin is trying to edit their own profile
        const currentUserId = localStorage.getItem('userId');
        if (isCusAdmin && currentUserId && parseInt(currentUserId) === user.id) {
          setCanChangeRole(false);
          setFormData(prev => ({
            ...prev,
            role: user.role
          }));
          return;
        }

        try {
          const response = await checkUserDeletable(user.id);
          setCanChangeRole(response.data.canDelete);
          
          if (!response.data.canDelete) {
            setFormData(prev => ({
              ...prev,
              role: user.role
            }));
          }
        } catch (err) {
          // If endpoint doesn't exist (404), assume user CAN change role
          if (err.response && err.response.status === 404) {
            setCanChangeRole(true);
          } else {
            // For other errors, be conservative
            setCanChangeRole(false);
            setFormData(prev => ({
              ...prev,
              role: user.role
            }));
          }
        }
      };

      checkRoleChange();
    }
  }, [user, open, isCusAdmin]);

  // Validate form whenever data changes
  useEffect(() => {
    const validateForm = () => {
      // Phone validation
      const phoneValid = !formData.phone || isValidKenyanPhone(formData.phone);
      setPhoneError(phoneValid ? '' : 'Phone must be in format +254XXXXXXXXX');
      
      // Email validation
      const emailValid = !formData.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      
      setFormValid(phoneValid && emailValid);
    };
    
    validateForm();
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If trying to change role but not allowed, prevent change
    if (name === 'role' && !canChangeRole) {
      return;
    }

    if (name === 'phone') {
      // Format phone number when it changes
      const formattedPhone = formatKenyanPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if trying to change role when not allowed
      if (!canChangeRole && formData.role !== originalRole) {
        setError('Cannot change role: User has transaction history');
        setFormData(prev => ({
          ...prev,
          role: originalRole
        }));
        setLoading(false);
        return;
      }

      const response = await updateUser(user.id, formData);
      setSuccess('User updated successfully');
      onUpdate(response.data.user);
      
      // Close modal after successful update with a delay
      setTimeout(() => {
        handleClose();
        // Refresh the user list after successful update
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <AppDialog open={open} onClose={handleClose}>
      <AppDialogTitle onClose={handleClose} subtitle="Update user account details">
        Edit User
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {!canChangeRole && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isCusAdmin && localStorage.getItem('userId') && parseInt(localStorage.getItem('userId')) === user.id
                ? 'Customer admins cannot change their own role'
                : 'Role cannot be changed because user has transaction history'
              }
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Username"
            value={user.username}
            margin="normal"
            InputProps={{
              readOnly: true,
            }}
            disabled
          />
          
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Phone (optional)"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!phoneError}
            helperText={phoneError || "Format: +254XXXXXXXXX"}
            margin="normal"
            InputProps={{
              startAdornment: formData.phone && !formData.phone.startsWith('+') ? (
                <InputAdornment position="start">+</InputAdornment>
              ) : null
            }}
          />
          
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              label="Role"
              disabled={!canChangeRole}
            >
              <MenuItem value="clerk">Clerk</MenuItem>
              {!isCusAdmin && <MenuItem value="rider">Rider</MenuItem>}
              <MenuItem value="cus-admin">Customer Admin</MenuItem>
              {isSuperAdmin && <MenuItem value="super-admin">Super Admin</MenuItem>}
            </Select>
          </FormControl>
          
          {(isSuperAdmin || isCusAdmin) && formData.role !== 'super-admin' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Company</InputLabel>
              <Select
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                label="Company"
                required
                disabled={isCusAdmin} // Disable for cus-admin as they can only edit users in their own company
              >
                {companies.map(company => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </AppDialogContent>
        
        <AppDialogActions>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formValid}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default EditUserModal; 